import { Paperclip, Upload, X } from "lucide-react"
import * as React from "react"
import { ChangeEvent, useMemo, useRef } from "react"
import { UseFormRegisterReturn } from "react-hook-form"
import { Button } from "../atoms/Button"
import { cn } from "../utils"

type UploadInputProps = {
  className?: string
  register?: UseFormRegisterReturn
  placeholder: string | React.ReactNode
  fileName?: string
  accept?: HTMLInputElement["accept"]
  isError?: boolean
  hideRemoveButton?: boolean
  onDragEnter?: (e: React.DragEvent) => void
  onDragLeave?: (e: React.DragEvent) => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent) => void
}

const UploadInput = ({
  className,
  register,
  placeholder,
  fileName,
  accept,
  isError,
  hideRemoveButton,
  disabled,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  ...props
}: UploadInputProps & Omit<React.InputHTMLAttributes<HTMLInputElement>, "placeholder">) => {
  const inputRef = useRef<HTMLInputElement>(undefined)
  const [isDragging, setIsDragging] = React.useState(false)

  const onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    register?.onChange(ev)
    props.onChange?.(ev)
  }

  const handleDragIn = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
    onDragEnter?.(e)
  }

  const handleDragOut = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    onDragLeave?.(e)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDragOver?.(e)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    onDrop?.(e)

    const files = e.dataTransfer.files
    if (files && files.length > 0 && inputRef.current) {
      const dataTransfer = new DataTransfer()

      Array.from(files).forEach((file) => {
        dataTransfer.items.add(file)
      })

      inputRef.current.files = dataTransfer.files

      const mockEvent = {
        target: inputRef.current,
        currentTarget: inputRef.current,
        type: "change",
        bubbles: true,
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.ChangeEvent<HTMLInputElement>

      onChange(mockEvent)
    }
  }

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (!inputRef.current) return

    inputRef.current.files = null
    inputRef.current.value = ""

    const mockEvent = {
      target: inputRef.current,
      currentTarget: inputRef.current,
      type: "change",
      bubbles: true,
      preventDefault: () => {},
      stopPropagation: () => {},
    } as React.ChangeEvent<HTMLInputElement>

    register?.onChange(mockEvent)
    props.onChange?.(mockEvent)
  }

  const onDivKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      inputRef.current?.click()
    }
  }

  const hasFile = !!fileName

  const content = useMemo(() => {
    if (fileName)
      return <span className="break-words text-sm px-6 text-placeholder">{fileName}</span>
    if (typeof placeholder === "string")
      return <span className="break-words text-sm px-6 text-placeholder">{placeholder}</span>
    return placeholder
  }, [fileName, placeholder])

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "relative py-10 px-6 border-dashed rounded-lg border border-border hover:bg-muted focus text-center flex flex-col items-center gap-4 cursor-pointer focus-visible:outline-hidden focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-2 group bg-background",
          hasFile && "hover:bg-muted",
          isDragging && "border-solid border-card bg-muted",
          isError && "border-error",
          disabled && "cursor-default opacity-70 hover:bg-background",
          className,
        )}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={!disabled ? onDivKeyDown : undefined}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!disabled ? () => inputRef.current?.click() : undefined}
      >
        {!hasFile && (
          <Upload className={cn("text-placeholder w-6 h-6", hasFile && "group-hover:hidden")} />
        )}
        {hasFile && <Paperclip className="text-placeholder w-6 h-6" />}
        {content}
        <input
          type="file"
          className={"absolute inset-0 opacity-0 w-0 h-0 cursor-pointer"}
          {...register}
          ref={(e) => {
            inputRef.current = e ?? undefined
            register?.ref(e)
          }}
          onChange={onChange}
          tabIndex={-1}
          accept={accept}
        />
        {hasFile && !hideRemoveButton && (
          <Button
            onClick={handleRemoveFile}
            variant="ghost"
            className="absolute top-2 right-2"
            size="icon"
          >
            <X className="text-foreground w-5 h-5" />
          </Button>
        )}
      </div>
    </div>
  )
}
UploadInput.displayName = "UploadInput"

export { UploadInput }
